import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Task, TaskNote, Priority, TaskStatus, TaskCategory, CATEGORIES, CATEGORY_COLORS } from '../types';
import CountdownTimer from '../components/CountdownTimer';

const TODAY = new Date().toISOString().slice(0, 10);

function fmt(d: string) {
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, role } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [task, setTask] = useState<Task | null>(null);
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Edit state
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPriority, setEditPriority] = useState<Priority>('Medium');
  const [editCategory, setEditCategory] = useState<TaskCategory>('General');
  const [editStatus, setEditStatus] = useState<TaskStatus>('Pending');
  const [editProgress, setEditProgress] = useState(0);
  const [editDeadline, setEditDeadline] = useState('');
  const [editHasDeadline, setEditHasDeadline] = useState(false);
  const [editRecurrence, setEditRecurrence] = useState<Task['recurrence']>('none');
  const [moveToNextDay, setMoveToNextDay] = useState(false);

  const fetchTask = useCallback(async () => {
    try {
      const [t, notesData] = await Promise.all([
        api.get<Task>('/api/tasks/' + id),
        api.get<TaskNote[]>('/api/tasks/' + id + '/notes'),
      ]);

      setTask(t);
      setNotes(notesData ?? []);

      // Populate edit fields
      setEditTitle(t.title);
      setEditDesc(t.description ?? '');
      setEditPriority(t.priority);
      setEditCategory(t.category);
      setEditStatus(t.status);
      setEditProgress(t.progress);
      setEditHasDeadline(!!t.deadline);
      setEditDeadline(t.deadline ? t.deadline.slice(0, 16) : `${TODAY}T23:59`);
      setEditRecurrence(t.recurrence);
    } catch (err: any) {
      showToast(err.message || 'Task not found.', 'error');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => { fetchTask(); }, [fetchTask]);

  const handleSave = async () => {
    if (!task || !editTitle.trim()) { showToast('Title is required.', 'error'); return; }
    setSaving(true);

    const wasCompleted = editStatus === 'Completed' && task.status !== 'Completed';
    const nextDate = moveToNextDay
      ? (() => { const d = new Date(task.task_date); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })()
      : task.task_date;

    try {
      const updated = await api.patch<Task>('/api/tasks/' + task.id, {
        title: editTitle.trim(),
        description: editDesc.trim(),
        priority: editPriority,
        category: editCategory,
        status: editStatus,
        progress: editStatus === 'Completed' ? 100 : editProgress,
        deadline: editHasDeadline ? new Date(editDeadline).toISOString() : null,
        recurrence: editRecurrence,
        task_date: nextDate,
        completed_at: wasCompleted ? new Date().toISOString() : task.completed_at,
        updated_at: new Date().toISOString(),
      });

      setTask(updated);
      setEditing(false);
      setMoveToNextDay(false);
      showToast(wasCompleted ? '🎉 Task completed! Well done!' : 'Task updated.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    setDeleting(true);
    try {
      await api.delete('/api/tasks/' + task.id);
      showToast('Task deleted.', 'info');
      navigate('/tasks');
    } catch (err: any) {
      showToast(err.message || 'Delete failed.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || !task) return;
    setAddingNote(true);
    try {
      const note = await api.post<TaskNote>('/api/tasks/' + task.id + '/notes', { content: newNote.trim() });
      setNotes(prev => [...prev, note]);
      setNewNote('');
    } catch (err: any) {
      showToast(err.message || 'Failed to add note.', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    await api.delete('/api/tasks/' + id + '/notes/' + noteId);
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  if (loading) return (
    <div className="page"><div className="loading-detail">
      <div className="skeleton skeleton-title" /><div className="skeleton skeleton-text" />
    </div></div>
  );
  if (!task) return null;

  const canEdit = role === 'admin' || user?.id === task.assigned_developer_id;
  const canDelete = role === 'admin';
  const canReview = role === 'tester' && (user?.id === task.assigned_tester_id || task.review_status === 'ready_for_review');
  const catColor = CATEGORY_COLORS[task.category] ?? '#6b7280';

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        {!editing && (canEdit || canDelete) && (
          <div className="page-actions">
            {canEdit && <button className="btn btn-secondary" onClick={() => setEditing(true)}>✎ Edit</button>}
            {canDelete && (
              !confirmDelete ? (
                <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>🗑 Delete</button>
              ) : (
                <div className="confirm-delete">
                  <span>Sure?</span>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                    {deleting ? <span className="btn-spinner" /> : 'Yes'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="detail-card" style={{ maxWidth: 860 }}>
        {editing && canEdit ? (
          /* ── Edit Mode ── */
          <div className="edit-form">
            <div className="form-group">
              <label className="form-label">Title <span className="required">*</span></label>
              <input type="text" className="form-input" value={editTitle} onChange={e => setEditTitle(e.target.value)} maxLength={200} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={4} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-select" value={editCategory} onChange={e => setEditCategory(e.target.value as TaskCategory)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-select" value={editPriority} onChange={e => setEditPriority(e.target.value as Priority)}>
                  <option value="High">🔴 High</option>
                  <option value="Medium">🟡 Medium</option>
                  <option value="Low">🟢 Low</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={editStatus} onChange={e => setEditStatus(e.target.value as TaskStatus)}>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed ✓</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Recurrence</label>
                <select className="form-select" value={editRecurrence} onChange={e => setEditRecurrence(e.target.value as Task['recurrence'])}>
                  <option value="none">One-time</option>
                  <option value="daily">🔁 Daily</option>
                  <option value="weekly">📅 Weekly</option>
                </select>
              </div>
            </div>

            {editStatus !== 'Completed' && (
              <div className="form-group">
                <label className="form-label">Progress: <strong>{editProgress}%</strong></label>
                <input type="range" min={0} max={100} step={5}
                  value={editProgress} onChange={e => setEditProgress(Number(e.target.value))}
                  className="progress-slider progress-slider-full" />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <input type="checkbox" checked={editHasDeadline} onChange={e => setEditHasDeadline(e.target.checked)} style={{ marginRight: 8 }} />
                Set deadline
              </label>
              {editHasDeadline && (
                <input type="datetime-local" className="form-input" value={editDeadline}
                  onChange={e => setEditDeadline(e.target.value)} />
              )}
            </div>

            <div className="form-group">
              <label className="form-label move-day-label">
                <input type="checkbox" checked={moveToNextDay} onChange={e => setMoveToNextDay(e.target.checked)} style={{ marginRight: 8 }} />
                Move to tomorrow
                <span className="tooltip" data-tip="Moves this task to the next day's list">?</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setEditing(false); setMoveToNextDay(false); }}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="btn-spinner" /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          /* ── View Mode ── */
          <>
            <div className="detail-header">
              <div className="detail-badges">
                <span className="category-badge" style={{ background: catColor + '18', color: catColor, borderColor: catColor + '40' }}>
                  {task.category}
                </span>
                <span className={`priority-badge priority-${task.priority.toLowerCase()}`}>{task.priority}</span>
                <span className={`status-badge ${task.status === 'Completed' ? 'status-closed' : task.status === 'In Progress' ? 'status-in-progress' : 'status-open'}`}>
                  {task.status}
                </span>
                {task.carried_over && <span className="carried-badge">⏳ Carried over</span>}
                {task.recurrence !== 'none' && <span className="recurrence-badge">🔁 {task.recurrence}</span>}
              </div>
              <h1 className="detail-title" style={{ textDecoration: task.status === 'Completed' ? 'line-through' : 'none', opacity: task.status === 'Completed' ? 0.6 : 1 }}>
                {task.title}
              </h1>
              <p className="detail-meta">
                Created {fmt(task.created_at)}
                {task.completed_at && <> · Completed {fmt(task.completed_at)}</>}
                {task.original_date && <> · Originally due {task.original_date}</>}
              </p>
            </div>

            {/* Progress */}
            <div className="detail-section">
              <h2 className="section-title">Progress</h2>
              <div className="task-progress-detail">
                <div className="task-progress-track large">
                  <div className={`task-progress-fill ${task.status === 'Completed' ? 'progress-complete' : 'progress-active'}`}
                    style={{ width: `${task.progress}%` }} />
                </div>
                <span className="task-progress-pct-large">{task.progress}%</span>
              </div>
              {task.deadline && <CountdownTimer deadline={task.deadline} />}
            </div>

            {/* Description */}
            {task.description && (
              <div className="detail-section">
                <h2 className="section-title">Description</h2>
                <div className="detail-description">{task.description}</div>
              </div>
            )}

            {/* Notes */}
            <div className="detail-section">
              <h2 className="section-title">Notes &amp; Comments ({notes.length})</h2>
              <div className="notes-list">
                {notes.length === 0 && <p className="notes-empty">No notes yet. Add one below.</p>}
                {notes.map(note => (
                  <div key={note.id} className="note-item">
                    <p className="note-content">{note.content}</p>
                    <div className="note-meta">
                      <span>{fmt(note.created_at)}</span>
                      <button className="note-delete-btn" onClick={() => handleDeleteNote(note.id)} title="Delete note">×</button>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={handleAddNote} className="note-form">
                <textarea
                  className="form-textarea"
                  placeholder="Add a note or progress update…"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  rows={3}
                />
                <button type="submit" className="btn btn-primary" disabled={addingNote || !newNote.trim()}>
                  {addingNote ? <span className="btn-spinner" /> : 'Add Note'}
                </button>
              </form>
            </div>
            {canReview && (
              <div className="detail-section">
                <h2 className="section-title">Tester Review</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      await api.patch<Task>('/api/tasks/' + task.id, { status: 'Completed', review_status: 'approved', reviewed_by: user?.id });
                      fetchTask();
                    }}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      await api.patch<Task>('/api/tasks/' + task.id, { status: 'In Progress', review_status: 'rework_required', reviewed_by: user?.id });
                      fetchTask();
                    }}
                  >
                    Request Rework
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
